import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ApiError,
  api,
  can,
  getToken,
  loadCapabilities,
  refusalReason,
  roleNames,
  setToken,
  type Capability,
  type Me,
} from './lib/api'
import { Home } from './views/Home'
import { ProgrammeReport } from './views/ProgrammeReport'
import { DatesToCheck } from './views/DatesToCheck'
import { Vendors } from './views/Vendors'
import { VendorAdd } from './views/VendorAdd'
import { VendorInvite } from './views/VendorInvite'
import { Import } from './views/Import'
import { Assessments } from './views/Assessments'
import { Reports } from './views/Reports'
import { Incidents } from './views/Incidents'
import { Audit } from './views/Audit'
import { NoticeBell } from './components/Notices'
import { Risk } from './views/Risk'
import { RiskMatrixSettings } from './views/RiskMatrixSettings'
import { RiskSinks } from './views/RiskSinks'
import { RiskSources } from './views/RiskSources'
import { Portfolio } from './views/Portfolio'
import { VendorDetail } from './views/VendorDetail'
import { AssessmentReview } from './views/AssessmentReview'
import { Imports } from './views/Imports'
import { Campaigns } from './views/Campaigns'
import { CampaignDetail } from './views/CampaignDetail'
import { CampaignBatch } from './views/CampaignBatch'
import { Exceptions } from './views/Exceptions'
import { Findings } from './views/Findings'
import { Questionnaires } from './views/Questionnaires'
import { QuestionnaireStudio } from './views/QuestionnaireStudio'
import { QuestionnaireImport } from './views/QuestionnaireImport'
import { RiskModels } from './views/RiskModels'
import { RiskModelStudio } from './views/RiskModelStudio'
import { Sustain } from './views/Sustain'
import { Documents } from './views/Documents'
import { SecondFactorPanel } from './components/SecondFactor'
import { Evidence } from './views/Evidence'
import { Procurement } from './views/Procurement'
import { Exits } from './views/Exits'
import { ExitWorkspace } from './views/ExitWorkspace'
import { MyWork } from './views/MyWork'
import { WorkQueueBar } from './components/WorkQueueBar'
import { MyWorkCount } from './components/MyWorkCount'
import { SignIn } from './views/SignIn'
import { SettingsRoute } from './views/Settings'
import { isSettingsScreen } from './views/settings/entries'
import { NotYourJob } from './components/NotYourJob'
import { SuiteLinks } from './components/SuiteLinks'
import { MenuButton, useNarrowMenu } from './components/NarrowMenu'
import { loadScreens, onScreen, screensPersonaName } from './lib/screens'
import { focusPage } from './lib/pageFocus'
import {
  completeLaunch,
  completeSignIn,
  isLaunchPath,
  isLaunchSession,
  isSignedIn,
  loadAuthConfig,
  signOut,
  type AuthConfig,
} from './lib/auth'
import './styles/phase1.css'
import './styles/shell.css'
import './styles/refine.css'
import './styles/depth.css'
import './styles/laptop.css'
import { Icon, ThemeButton, ToastHost } from './components/ui'
import { noteOrigin } from './lib/origin'
import { arriveAtEntry, keepScrollPlace } from './lib/scrollPlace'
import { QuickSearch } from './components/QuickSearch'
import { Toggletip, useTiltAll } from './components/kit'

// Five sections. Home says what needs you, Vendors is the register,
// Assessments is everything in flight, Reports is what you take to a meeting,
// Settings is everything you set up once. Every older screen still exists
// and still answers at its old address; it is reached from one of the five
// rather than lined up beside them.

export type Route =
  | { name: 'home' }
  // Everything waiting for the person signed in, in one list (release 3.2).
  | { name: 'my-work' }
  | { name: 'not-found'; path: string }
  | { name: 'vendors' }
  | { name: 'vendor-add' }
  | { name: 'import' }
  | { name: 'vendor-invite'; id: string }
  | { name: 'assessments' }
  | { name: 'incidents' }
  | { name: 'incident'; id: string }
  | { name: 'onboarding-process' }
  | { name: 'audit' }
  | { name: 'risk' }
  | { name: 'risk-matrix' }
  | { name: 'risk-sinks' }
  | { name: 'risk-sources' }
  // The programme report (#/report/programme) is the reports screen's, not a
  // screen of its own: whoever may read reports may print it.
  // So is "Dates to check" (#/report/dates-to-check, release 3.3, M4).
  | { name: 'reports'; report?: 'programme' | 'dates-to-check' }
  | { name: 'settings' }
  | { name: 'portfolio' }
  | { name: 'vendor'; id: string }
  | { name: 'vendor-record'; id: string; tab?: string }
  | { name: 'assessment'; id: string }
  | { name: 'imports' }
  | { name: 'campaigns' }
  | { name: 'campaign'; id: string }
  // The bulk send that proposes a baseline per vendor (release 3.4, M7):
  // #/campaigns/new-batch, and one already raised at ?id=.
  | { name: 'campaign-batch' }
  | { name: 'exceptions' }
  | { name: 'findings' }
  | { name: 'custom-fields' }
  | { name: 'questionnaires' }
  | { name: 'questionnaire'; id: string }
  | { name: 'questionnaire-import' }
  | { name: 'risk-models' }
  | { name: 'risk-model'; id: string }
  | { name: 'approval-matrix' }
  // The due diligence parameters and who may approve an exception at each
  // severity, at #/settings/due-diligence and #/settings/exception-authority.
  | { name: 'due-diligence' }
  | { name: 'exception-authority' }
  | { name: 'sustain' }
  | { name: 'evidence' }
  | { name: 'documents' }
  | { name: 'users' }
  | { name: 'procurement' }
  | { name: 'exits' }
  | { name: 'exit-workspace'; id: string }

// What each screen needs from the roles. Home is session.read since release
// 3.1 (persona spec 6.3, T2): everybody who signs in has a Home, narrowed
// section by section by the API, and the IT admin, who reads no vendor, lands
// on its own home rather than on Settings.
const SCREEN_NEEDS: Record<Route['name'], Capability> = {
  home: 'session.read',
  // Each row in it is filtered by the API to work the reader may do, so the
  // screen itself needs only a sign-in.
  'my-work': 'session.read',
  vendors: 'vendor.read',
  'vendor-add': 'vendor.create',
  import: 'vendor.import',
  'vendor-invite': 'questionnaire.issue',
  assessments: 'assessment.read',
  // An address nothing answers to is nobody's job and says so to everybody,
  // including a reader who holds no vendor read.
  'not-found': 'session.read',
  incidents: 'vendor.read',
  incident: 'vendor.read',
  'onboarding-process': 'config.read',
  audit: 'audit.read',
  risk: 'vendor.read',
  'risk-matrix': 'config.read',
  'risk-sinks': 'config.read',
  'risk-sources': 'config.read',
  reports: 'reports.read',
  documents: 'vendor.read',
  settings: 'session.read',
  portfolio: 'vendor.read',
  vendor: 'vendor.read',
  assessment: 'assessment.read',
  imports: 'vendor.read',
  campaigns: 'assessment.read',
  campaign: 'assessment.read',
  // Reading a raised bulk send is a campaign read; creating one asks
  // assessment.plan and approving it questionnaire.issue, on the screen and in
  // the API.
  'campaign-batch': 'assessment.read',
  // session.read, because the executive decides a critical exception and
  // holds reports.read and nothing else; the screen then shows only the
  // exceptions waiting on them (the API decides which).
  exceptions: 'session.read',
  findings: 'assessment.read',
  questionnaires: 'config.read',
  questionnaire: 'config.read',
  'questionnaire-import': 'config.read',
  'risk-models': 'config.read',
  'risk-model': 'config.read',
  'approval-matrix': 'config.read',
  'due-diligence': 'config.read',
  'exception-authority': 'config.read',
  sustain: 'assessment.read',
  evidence: 'audit.read',
  users: 'user.administer',
  'custom-fields': 'config.read',
  'vendor-record': 'vendor.read',
  procurement: 'vendor.read',
  exits: 'vendor.read',
  'exit-workspace': 'vendor.read',
}

type Leaf = { href: string; label: string; screen: Route['name'] }
type Section = {
  href: string
  label: string
  icon: string
  screen: Route['name']
  current: Route['name'][]
  // What is inside this section. Shown under it while the reader is in it.
  //
  // # WHY THIS EXISTS
  //
  // Six words in a sidebar and forty screens behind them reads as a product
  // with six screens. Everything this system does beyond a register and a
  // matrix - the campaigns, the findings, the accepted risks, the reassessment
  // clock, the incidents, the document register, the evidence chain, the audit
  // trail - was reachable but never named anywhere a person looks first, so
  // the product was judged on the six words.
  //
  // The six sections do not change. Each one now says what is inside it, and
  // only while you are in it, so the sidebar is still six lines from anywhere
  // else. Every leaf is gated by its own screen's capability exactly as the
  // section is, so a reader is never shown a door that will not open.
  under?: Leaf[]
}

const NAV: Section[] = [
  { href: '#/home', label: 'Home', icon: 'home', screen: 'home', current: ['home'] },
  // One list of everything waiting for the reader (release 3.2, rule 9: each
  // task lives in one place). The Sign-offs list, the exit badges and the
  // bell's own list were three partial copies of it. The entry opens the whole
  // list, which is what its badge counts (lib/myWork.ts MY_WORK_ALL).
  { href: '#/my-work?show=all', label: 'My work', icon: 'inbox', screen: 'my-work', current: ['my-work'] },
  {
    href: '#/vendors',
    label: 'Vendors',
    icon: 'vendors',
    screen: 'vendors',
    // Objects in the menu, workflows inside them (release 3.2, duplication map
    // section 4). Incidents are about a vendor, not an assessment, so they sit
    // here. Bring vendors in is a workflow, started from the register's own
    // header like Add a vendor, so it is not a menu item of its own.
    current: ['vendors', 'portfolio', 'vendor', 'vendor-record', 'vendor-add', 'vendor-invite', 'import', 'imports', 'documents', 'procurement', 'exits', 'exit-workspace', 'incidents', 'incident'],
    under: [
      { href: '#/vendors', label: 'Register', screen: 'vendors' },
      { href: '#/portfolio', label: 'Portfolio', screen: 'portfolio' },
      { href: '#/procurement', label: 'Purchase requests', screen: 'procurement' },
      { href: '#/exits', label: 'Offboarding', screen: 'exits' },
      { href: '#/documents', label: 'Documents', screen: 'documents' },
      { href: '#/incidents', label: 'Incidents', screen: 'incidents' },
    ],
  },
  {
    href: '#/assessments',
    label: 'Assessments',
    icon: 'assessments',
    screen: 'assessments',
    current: ['assessments', 'assessment', 'campaigns', 'campaign', 'campaign-batch', 'findings', 'exceptions', 'sustain', 'questionnaires', 'questionnaire', 'questionnaire-import'],
    // Each item is named as its page is titled, so the menu and the page never
    // use two names for one screen ("In flight" opened "Assessments").
    under: [
      { href: '#/assessments', label: 'Assessments', screen: 'assessments' },
      { href: '#/campaigns', label: 'Bulk assessments', screen: 'campaigns' },
      { href: '#/questionnaires', label: 'Questionnaires', screen: 'questionnaires' },
      { href: '#/findings', label: 'Findings', screen: 'findings' },
      { href: '#/exceptions', label: 'Risk acceptances', screen: 'exceptions' },
      { href: '#/sustain', label: 'Reassessment', screen: 'sustain' },
    ],
  },
  {
    href: '#/risk',
    label: 'Risk',
    icon: 'shield',
    screen: 'risk',
    current: ['risk', 'risk-matrix', 'risk-sinks', 'risk-sources', 'risk-models', 'risk-model'],
    under: [
      { href: '#/risk', label: 'Risk', screen: 'risk' },
      { href: '#/risk-matrix', label: 'Risk matrix', screen: 'risk-matrix' },
      { href: '#/risk-models', label: 'Risk models', screen: 'risk-models' },
      { href: '#/risk-sources', label: 'Import risks', screen: 'risk-sources' },
      { href: '#/risk-sinks', label: 'Send risks to registers', screen: 'risk-sinks' },
    ],
  },
  {
    href: '#/reports',
    label: 'Reports',
    icon: 'reports',
    screen: 'reports',
    current: ['reports', 'evidence', 'audit'],
    under: [
      { href: '#/reports', label: 'Reports', screen: 'reports' },
      { href: '#/evidence', label: 'Evidence chain', screen: 'evidence' },
      { href: '#/audit', label: 'Audit trail', screen: 'audit' },
    ],
  },
  {
    href: '#/settings',
    label: 'Settings',
    icon: 'settings',
    screen: 'settings',
    // No list under Settings. Its own screen is the index, eighteen areas
    // arranged as cards, and repeating five of them in a sidebar would say
    // those five matter more than the rest, which is not true.
    current: [
      'settings',
      'approval-matrix',
      'due-diligence',
      'exception-authority',
      'custom-fields',
      'onboarding-process',
      'users',
    ],
  },
]

// The persona spec's id for a screen (X0 test hooks): data-nav-id on every
// entry that opens it and data-page on the page it renders, so an automated
// walk can compare the menu and the pages with the spec rather than with this
// file.
export function navId(screen: string): string {
  return 'tprm.' + screen
}

// Whether a screen is offered: the roles may open it and it is part of the
// signed-in persona's job (lib/screens.ts). Both, because the menu is where a
// reader looks for their work, and a door to somebody else's job is clutter
// even where the roles would let it open.
export function mayOpen(screen: Route['name']): boolean {
  return can(SCREEN_NEEDS[screen]) && onScreen(screen)
}

// The menu entry a screen that has none of its own belongs under: a vendor's
// record under Register, one campaign under Campaigns. Only the offboarding
// workspace used to be mapped, so on a vendor's record the Vendors section was
// lit and nothing inside it was, and the menu did not say where the reader was.
const ENTRY_FOR: Partial<Record<Route['name'], Route['name']>> = {
  vendor: 'vendors',
  'vendor-record': 'vendors',
  'vendor-invite': 'vendors',
  import: 'vendors',
  imports: 'vendors',
  'exit-workspace': 'exits',
  incident: 'incidents',
  assessment: 'assessments',
  campaign: 'campaigns',
  'campaign-batch': 'campaigns',
  questionnaire: 'questionnaires',
  'questionnaire-import': 'questionnaires',
  'risk-model': 'risk-models',
}

// What a reader might call a screen when they do not know its name: the words
// "Jump to" answers to, beside the name on the menu. A reader looking for the
// waiver they signed types "waiver" and is offered Risk acceptances. The
// menu's old names stay here as other words, so a reader who learned them
// still finds the screen.
const JUMP_WORDS: Partial<Record<Route['name'], string>> = {
  home: 'dashboard overview start programme health',
  'my-work': 'tasks inbox approvals pending to do sign offs waiting',
  'vendor-add': 'new vendor onboard create',
  vendors: 'suppliers third parties list register',
  portfolio: 'filter segment search tier band',
  procurement: 'procurement intake request buy new supplier',
  exits: 'exit terminate leave end contract',
  documents: 'contracts certificates files policies',
  incidents: 'breach outage event notification',
  assessments: 'reviews due diligence in flight answers',
  campaigns: 'campaigns bulk send questionnaire many vendors',
  questionnaires: 'templates questions forms',
  findings: 'issues gaps observations remediation owed',
  exceptions: 'waiver exception accepted risks',
  sustain: 'periodic review renewal due again',
  risk: 'register heat map open risks',
  'risk-matrix': 'thresholds appetite likelihood impact',
  'risk-models': 'scoring weights bands model',
  'risk-sources': 'import other registers sources',
  'risk-sinks': 'export destinations group register',
  reports: 'board programme export summary',
  evidence: 'proof integrity chain hash',
  audit: 'log history who did what',
  settings: 'configuration sso email users people roles approval matrix custom fields workflows',
}

export type JumpEntry = { href: string; label: string; screen: Route['name']; within?: string }

// Every screen the menu can open for this reader, section or not, so "Jump to"
// offers what the sidebar would and nothing it would not.
export function jumpEntries(sections: Section[]): JumpEntry[] {
  const out: JumpEntry[] = []
  if (can('vendor.create') && onScreen('vendor-add')) out.push({ href: '#/vendor/new', label: 'Add a vendor', screen: 'vendor-add' })
  for (const e of sections) {
    out.push({ href: e.href, label: e.label, screen: e.screen })
    for (const u of e.under ?? []) {
      if (u.href !== e.href && mayOpen(u.screen)) out.push({ href: u.href, label: u.label, screen: u.screen, within: e.label })
    }
  }
  return out
}

// The screens a phrase finds: every word typed starts a word of the screen's
// name, of its section, or of what else it is called. Names first.
export function jumpResults(entries: JumpEntry[], query: string, limit = 7): JumpEntry[] {
  const plain = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
  const typed = plain(query).split(' ').filter(Boolean)
  if (typed.length === 0) return []
  const starts = (text: string, w: string) => (' ' + text).includes(' ' + w)
  const found: { entry: JumpEntry; score: number }[] = []
  for (const entry of entries) {
    const label = plain(entry.label)
    const around = plain((entry.within ?? '') + ' ' + (JUMP_WORDS[entry.screen] ?? ''))
    if (!typed.every((w) => starts(label, w) || starts(around, w))) continue
    found.push({ entry, score: (label.startsWith(typed[0] ?? '') ? 2 : 0) + (typed.every((w) => starts(label, w)) ? 1 : 0) })
  }
  return found.sort((x, y) => y.score - x.score).slice(0, limit).map((f) => f.entry)
}

// "Jump to": type what you are after and go there (10 Oct 2026, as in FOCTTA
// Consent and Discovery). Forty screens behind six words is quick for a reader
// who knows which word; this is for the one who knows only what they want.
// Enter opens the first match, Escape clears, "/" puts the cursor here unless
// the reader is already typing somewhere.
function NavJump({ sections, onChosen }: { sections: Section[]; onChosen: (already: boolean) => void }) {
  const [q, setQ] = useState('')
  const input = useRef<HTMLInputElement>(null)
  const found = useMemo(() => jumpResults(jumpEntries(sections), q), [sections, q])
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
      const at = document.activeElement
      if (at instanceof HTMLInputElement || at instanceof HTMLTextAreaElement || at instanceof HTMLSelectElement) return
      if (at instanceof HTMLElement && at.isContentEditable) return
      if (document.querySelector('[role="dialog"], [aria-modal="true"]')) return
      // Not on the page below 1024 px while the menu is closed (an overlay
      // there, NarrowMenu.tsx): the key is the reader's then.
      if (!input.current || input.current.offsetParent === null) return
      if (getComputedStyle(input.current).visibility !== 'visible') return
      e.preventDefault()
      input.current.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  const asked = q.trim() !== ''
  // The list goes when a screen is chosen, and with it the link that had the
  // focus. The sidebar puts the focus on the page that was asked for.
  // `already`: the screen chosen is the one open, so the address does not
  // change and nothing else will close the menu under 1024 px.
  const chosen = (already: boolean) => {
    setQ('')
    onChosen(already)
  }
  return (
    <div className="nav-jump" role="search">
      <label className="vh" htmlFor="nav-jump">
        Jump to a screen
      </label>
      <input
        id="nav-jump"
        ref={input}
        data-testid="nav-jump"
        type="search"
        autoComplete="off"
        placeholder="Jump to"
        title="Type what you are after: a screen's name, or what you would call it. Press / to come here."
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          // Escape clears what was typed; with nothing typed it is left to
          // the menu, which closes under 1024 px (NarrowMenu.tsx).
          if (e.key === 'Escape' && q !== '') {
            e.preventDefault()
            setQ('')
          }
          if (e.key === 'Enter' && found[0]) {
            const already = window.location.hash === found[0].href
            window.location.hash = found[0].href
            chosen(already)
          }
        }}
      />
      {asked && (
        <ul className="nav-jump-list" aria-label="Screens that match">
          {found.map((f) => (
            <li key={f.href}>
              <a href={f.href} onClick={() => chosen(window.location.hash === f.href)}>
                {f.label}
                {f.within && <span className="in"> in {f.within}</span>}
              </a>
            </li>
          ))}
          {found.length === 0 && <li className="nav-jump-none">No screen answers to that</li>}
        </ul>
      )}
    </div>
  )
}

// The sidebar: "Add a vendor", the sections the reader may open and, inside
// the section they are in, its screens. Every entry carries data-nav-id, the
// persona spec's id for the screen it opens (X0 test hooks), so the persona
// walk compares the menu with the spec rather than with this file.
//
// One entry is the page (aria-current="page"), never two. The section the
// reader is in is drawn as one block with its screens inside it; when one of
// those screens is the page the section's own line is its heading, not a
// second highlight competing with it.
export function SideNav({ route, sections, onLeave }: { route: Route; sections: Section[]; onLeave?: () => void }) {
  const entry = ENTRY_FOR[route.name] ?? route.name
  // The sections scroll inside the rail when the window is short, so the entry
  // that is the page is brought into view: where the reader is must not be
  // somewhere they have to scroll to.
  const nav = useRef<HTMLElement>(null)
  // After a Jump to choice the focus goes to the page asked for, where the
  // skip link puts it. It is moved here, once the new page is in the document:
  // moved from the click, it could land on the page being replaced and be lost
  // with it. Choosing the screen already open changes no route, so a short
  // wait covers that case.
  const jumped = useRef(false)
  const focusJumped = () => {
    if (!jumped.current) return
    jumped.current = false
    focusPage()
  }
  useEffect(() => {
    nav.current?.querySelector('[aria-current="page"]')?.scrollIntoView({ block: 'nearest' })
    focusJumped()
  }, [route.name])
  return (
    <>
      {can('vendor.create') && onScreen('vendor-add') && (
        <a
          className="button primary side-add"
          href="#/vendor/new"
          aria-current={route.name === 'vendor-add' ? 'page' : undefined}
          data-nav-id={navId('vendor-add')}
        >
          <Icon name="plus" size={16} /> Add a vendor
        </a>
      )}
      <NavJump
        sections={sections}
        onChosen={(already) => {
          jumped.current = true
          window.setTimeout(focusJumped, 300)
          // The screen already open: no address change closes the overlay.
          if (already) onLeave?.()
        }}
      />
      <nav className="side-nav" aria-label="Sections" ref={nav}>
        {sections.map((e) => {
          const here = e.current.includes(route.name)
          const inside = here ? (e.under ?? []).filter((u) => mayOpen(u.screen)) : []
          const pageIsInside = inside.some((u) => u.screen === entry)
          const pageIsTheButton = here && route.name === 'vendor-add'
          return (
            <div key={e.href} className={'nav-section' + (here ? ' here' : '') + (inside.length > 0 ? ' open' : '')}>
              <a
                href={e.href}
                className="nav-head"
                aria-current={here && !pageIsInside && !pageIsTheButton ? 'page' : undefined}
                data-nav-id={navId(e.screen)}
              >
                <Icon name={e.icon} /> {e.label}
                {e.screen === 'my-work' && <MyWorkCount />}
              </a>
              {inside.length > 0 && (
                <div className="nav-under">
                  {inside.map((u) => (
                    <a
                      key={u.href}
                      href={u.href}
                      data-nav-id={navId(u.screen)}
                      aria-current={u.screen === entry ? 'page' : undefined}
                      className={u.screen === entry ? 'on' : undefined}
                    >
                      {u.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </nav>
    </>
  )
}

// Every screen the sidebar can offer this reader, whichever section they are
// in: the sections they may open, the screens inside those, and "Add a
// vendor". The persona test compares this with the persona spec.
export function reachableSections(): Section[] {
  return NAV.filter((e) => mayOpen(e.screen))
}

// Where each job lands when it opens the console (release 3.2, design rule 4):
// the analyst on the reviews waiting for them and the approver on the
// decisions, both in My work; everybody else on Home, which for management is
// the short overview. A job that cannot open My work lands where it always
// did, on the first section it may open. The landing carries no filter, so My
// work opens on the job's own filter; every count links to the whole list.
const LANDING: Record<string, string> = { analyst: '#/my-work', approver: '#/my-work' }

export function landingFor(personaCode: string | undefined): string {
  const want = LANDING[personaCode ?? '']
  if (want && mayOpen('my-work')) return want
  return NAV.find((e) => mayOpen(e.screen))?.href ?? '#/settings'
}

export function navScreens(): string[] {
  const out: string[] = []
  if (can('vendor.create') && onScreen('vendor-add')) out.push('vendor-add')
  for (const e of reachableSections()) {
    out.push(e.screen)
    for (const u of e.under ?? []) {
      if (u.screen !== e.screen && mayOpen(u.screen)) out.push(u.screen)
    }
  }
  return out
}

export function parseHash(hash: string): Route {
  const path = hash.replace(/^#\/?/, '').split('?')[0] ?? ''
  const parts = path.split('/').filter(Boolean)
  const id = parts[1] ?? ''
  switch (parts[0]) {
    case undefined:
    case '':
    case 'home':
      return { name: 'home' }
    case 'my-work':
      return { name: 'my-work' }
    case 'vendors':
      return { name: 'vendors' }
    case 'import':
      return { name: 'import' }
    case 'portfolio':
      return { name: 'portfolio' }
    case 'vendor':
      // #/vendor/new/full was the orphaned full creation form; it opens the
      // one intake wizard now (see redirectFor).
      if (id === 'new') return { name: 'vendor-add' }
      if (id && parts[2] === 'invite') return { name: 'vendor-invite', id }
      if (id && parts[2] === 'full') return { name: 'vendor-record', id, tab: parts[3] }
      if (id) return { name: 'vendor', id }
      return { name: 'vendors' }
    case 'overview':
      // The overview screen was removed on 21 September 2026: it repeated
      // Home and the registers, and its breakdowns are on Reports. A link to it
      // somebody kept lands on Home rather than on a page that is not there.
      return { name: 'home' }
    case 'incidents':
      return { name: 'incidents' }
    case 'incident':
      return id ? { name: 'incident', id: decodeURIComponent(id) } : { name: 'incidents' }
    case 'onboarding-process':
      return { name: 'onboarding-process' }
    case 'audit':
      return { name: 'audit' }
    case 'risk':
      return { name: 'risk' }
    case 'risk-matrix':
      return { name: 'risk-matrix' }
    case 'risk-sinks':
      return { name: 'risk-sinks' }
    case 'risk-sources':
      return { name: 'risk-sources' }
    case 'connections':
      // The Integrations tab in Settings answers this now. The route stays
      // because links to it exist in the wild, and it sends somebody to the
      // one screen rather than to a second copy of it.
      return { name: 'settings' }
    case 'mailboxes':
      // Folded into the email tab. A link somebody already sent still lands
      // somewhere useful rather than on a not-found page.
      return { name: 'settings' }
    case 'assessments':
      return { name: 'assessments' }
    case 'assessment':
      return id ? { name: 'assessment', id } : { name: 'assessments' }
    case 'reports':
      return { name: 'reports' }
    case 'report':
      if (id === 'dates-to-check') return { name: 'reports', report: 'dates-to-check' }
      return id === 'programme' ? { name: 'reports', report: 'programme' } : { name: 'reports' }
    case 'settings':
      if (id === 'due-diligence') return { name: 'due-diligence' }
      if (id === 'exception-authority') return { name: 'exception-authority' }
      return { name: 'settings' }
    case 'imports':
      return { name: 'imports' }
    case 'campaigns':
      if (id === 'new-batch') return { name: 'campaign-batch' }
      return { name: 'campaigns' }
    case 'campaign':
      return id ? { name: 'campaign', id } : { name: 'campaigns' }
    case 'exceptions':
      return { name: 'exceptions' }
    case 'findings':
      return { name: 'findings' }
    case 'custom-fields':
      return { name: 'custom-fields' }
    case 'questionnaires':
      return { name: 'questionnaires' }
    case 'questionnaire':
      if (id === 'import') return { name: 'questionnaire-import' }
      return id ? { name: 'questionnaire', id } : { name: 'questionnaires' }
    case 'risk-models':
      return { name: 'risk-models' }
    case 'risk-model':
      return id ? { name: 'risk-model', id } : { name: 'risk-models' }
    case 'approval-matrix':
      return { name: 'approval-matrix' }
    case 'sustain':
      return { name: 'sustain' }
    case 'evidence':
      return { name: 'evidence' }
    case 'documents':
      return { name: 'documents' }
    case 'users':
      return { name: 'users' }
    case 'procurement':
      return { name: 'procurement' }
    case 'exits':
      return id ? { name: 'exit-workspace', id } : { name: 'exits' }
    case 'signoffs':
      // Sign-offs was a filtered copy of the exits waiting for the reader's
      // signature. Those are rows of My work now; an old link lands there,
      // on the decisions (see redirectFor).
      return { name: 'my-work' }
    default:
      // An address nothing answers to. Going Home here hid every broken
      // link behind a screen that looked fine; saying so is the whole fix.
      return { name: 'not-found', path: '#/' + path }
  }
}

// Addresses that moved, and where each one lives now. parseHash already opens
// the new screen for them; this also corrects the address bar, replaced rather
// than pushed, so a copied link, Back and a reload all show where the reader
// actually is (release 3.2: every old route stays reachable, none is a dead
// link).
const MOVED: { from: RegExp; to: string }[] = [
  // The exits waiting for the reader's signature are decisions in My work.
  { from: /^#\/signoffs\/?(\?.*)?$/, to: '#/my-work?show=decide' },
  // The second, longer creation form re-asked every intake field and no
  // screen linked to it. The one intake wizard is Add a vendor.
  { from: /^#\/vendor\/new\/full\/?(\?.*)?$/, to: '#/vendor/new' },
  // The connections and mailboxes screens became Settings entries. Until the
  // Settings index (13 Oct 2026) both landed on Organisation, the first entry;
  // then on the index. Each opens its own entry now.
  { from: /^#\/connections\/?(\?.*)?$/, to: '#/settings?t=integrations' },
  { from: /^#\/mailboxes\/?(\?.*)?$/, to: '#/settings?t=email' },
]

export function redirectFor(hash: string): string | null {
  for (const m of MOVED) if (m.from.test(hash)) return m.to
  return null
}

function useHashRoute(): Route {
  const [route, setRoute] = useState<Route>(() => {
    const moved = redirectFor(window.location.hash)
    if (moved) history.replaceState(history.state, '', moved)
    return parseHash(window.location.hash)
  })
  useEffect(() => {
    // Back model (release 3.3): a record remembers the list it was opened
    // from (B4, lib/origin.ts), and Back returns to the place on the page the
    // person left, a new page starting at the top (B6, lib/scrollPlace.ts).
    const stop = keepScrollPlace()
    const sync = (e: HashChangeEvent) => {
      const moved = redirectFor(window.location.hash)
      if (moved) history.replaceState(history.state, '', moved)
      const now = window.location.hash
      // The address left, as it was when it was left: a list's filters live in
      // its address and are rewritten with replaceState, which raises no event.
      let from = ''
      try {
        from = new URL(e.oldURL).hash
      } catch {
        from = ''
      }
      noteOrigin(from, now)
      setRoute(parseHash(now))
      arriveAtEntry()
    }
    window.addEventListener('hashchange', sync)
    return () => {
      stop()
      window.removeEventListener('hashchange', sync)
    }
  }, [])
  return route
}

const SIDE_PINNED_KEY = 'tprm.side.pinned'

export default function App() {
  const route = useHashRoute()
  const [moving, setMoving] = useState(false)
  useEffect(() => {
    setMoving(true)
    const t = window.setTimeout(() => setMoving(false), 650)
    return () => window.clearTimeout(t)
  }, [route])
  // The exit board's cards lean toward the pointer like the kit's tiles. They
  // are drawn by the exit screens, so the shell arms them by class rather than
  // the board wiring the kit in.
  useTiltAll('.exit-card', { max: 5 })
  const [me, setMe] = useState<Me | null>(null)
  const [meError, setMeError] = useState<ApiError | null>(null)
  const [tokenOpen, setTokenOpen] = useState(false)
  // The person's own second factor (release 3.1), for TPRM's own accounts.
  const [mfaOpen, setMfaOpen] = useState(false)
  const [draftToken, setDraftToken] = useState(() => getToken())
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null)
  const [authNotice, setAuthNotice] = useState('')
  const [authSettled, setAuthSettled] = useState(false)
  // Below 1440 px the side menu folds to icons (styles/laptop.css); a person
  // who wants it open keeps it open, remembered in this browser.
  const [sidePinned, setSidePinned] = useState(() => {
    try {
      return localStorage.getItem(SIDE_PINNED_KEY) === '1'
    } catch {
      return false
    }
  })
  // Below 1024 px the side menu is an overlay, closed until the Menu button
  // in the top bar opens it (components/NarrowMenu.tsx).
  const menu = useNarrowMenu(route)
  const pinSide = (on: boolean) => {
    setSidePinned(on)
    try {
      localStorage.setItem(SIDE_PINNED_KEY, on ? '1' : '0')
    } catch {
      // A browser that refuses storage keeps the choice for this visit.
    }
  }

  const loadMe = useCallback(() => {
    api
      .me()
      .then(async (m) => {
        await loadCapabilities(m.roles)
        loadScreens(m)
        setMe(m)
        setMeError(null)
      })
      .catch((err) => {
        setMe(null)
        setMeError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err)))
      })
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        // A reader arriving from Consent carries a launch code, not a sign-in
        // code, and the two are never both in one address.
        const outcome = isLaunchPath() ? await completeLaunch() : await completeSignIn()
        if (!cancelled && outcome.status === 'failed') setAuthNotice(outcome.message)
        const cfg = await loadAuthConfig()
        if (!cancelled) setAuthConfig(cfg)
      } catch (err) {
        if (!cancelled) setAuthNotice(err instanceof Error ? err.message : String(err))
      } finally {
        if (!cancelled) setAuthSettled(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (authSettled) loadMe()
  }, [authSettled, loadMe])

  // Where a reader lands, decided once their capabilities are known.
  //
  // Home needs vendor.read, and the console used to go there whatever the
  // reader held, so Management, whose whole job is reading reports, met a
  // refusal as its first screen. That reads as a broken product rather than
  // as a role working exactly as the matrix says it should. can() answers
  // true until the catalogue arrives, so this waits for it.
  useEffect(() => {
    if (!me) return
    const at = window.location.hash
    if (at !== '' && at !== '#' && at !== '#/') return
    window.location.hash = landingFor(me.persona?.code)
  }, [me])

  function applyToken() {
    setToken(draftToken)
    setTokenOpen(false)
    window.location.reload()
  }

  const isDev = me?.authMode === 'dev'
  const reachable = meError ? [] : reachableSections()
  // Where the logo goes, and where a lost reader is sent back to. Home for
  // almost everybody; for a role that cannot open Home, the first screen it
  // can.
  const start = reachable[0]?.href ?? '#/settings'
  // Two different walls. The roles decide whether a screen may open at all; the
  // persona decides whether it is this account's job. A screen the roles allow
  // and the job does not says so and sends the reader back, rather than
  // repeating a refusal about capabilities they do hold.
  const rolesAllow = can(SCREEN_NEEDS[route.name])
  const openHere = rolesAllow && onScreen(route.name)
  const needsSignIn =
    authSettled && authConfig != null && authConfig.mode === 'oidc' && (!isSignedIn() || meError?.status === 401)

  if (!authSettled) {
    return (
      <div className="signin">
        <div className="panel signin-card">
          <p className="muted">One moment.</p>
        </div>
      </div>
    )
  }
  if (needsSignIn && authConfig != null) {
    // A session opened from Consent that has stopped working was ended there,
    // or ran out. The way back is Consent's button, not this sign-in page, and
    // the reader is told so rather than left wondering which to use.
    const ended =
      isLaunchSession() && meError?.status === 401
        ? 'Your session from Consent has ended. Open third-party risk from Consent again, or sign in here.'
        : ''
    return <SignIn config={authConfig} notice={authNotice || ended} />
  }
  if (me === null && meError === null) {
    return (
      <div className="signin">
        <div className="panel signin-card">
          <p className="muted">Signing you in.</p>
        </div>
      </div>
    )
  }

  return (
    <ToastHost>
    <div className={'app' + (sidePinned ? ' side-pinned' : '') + (menu.open ? ' menu-open' : '')}>
      <button type="button" className="skip-link" onClick={() => focusPage()} inert={menu.open || undefined}>
        Skip to main content
      </button>
      <aside className="side" {...menu.panel}>
        <button type="button" className="side-close" onClick={menu.dismiss} aria-label="Close the menu" title="Close the menu">
          <Icon name="close" size={18} />
        </button>
        <a className="brand" href={start}>
          <img className="brand-mark brand-mark-light" src="/foctta-header-logo.png" alt="FOCTTA" />
          <img className="brand-mark brand-mark-dark" src="/foctta-header-logo-dark.png" alt="FOCTTA" />
          <span className="brand-product">TPRM</span>
        </a>
        {me && <SideNav route={route} sections={reachable} onLeave={menu.leave} />}
        <div className="side-foot">
          {me && (
            <span className="who">
              <strong>{me.tenant.displayName || me.tenant.legalName}</strong>
              <span>{me.persona?.name ?? roleNames(me.roles).join(', ')}</span>
            </span>
          )}
          <ThemeButton />
          <button
            type="button"
            className="side-fold"
            onClick={() => pinSide(!sidePinned)}
            aria-pressed={sidePinned}
            title={sidePinned ? 'Fold the menu to icons' : 'Keep the menu open'}
            aria-label={sidePinned ? 'Fold the menu to icons' : 'Keep the menu open'}
          >
            <Icon name="chevron" size={16} />
          </button>
          {authConfig?.mode === 'oidc' && authConfig.ready && authConfig.accounts === true && (
            <button
              type="button"
              data-action="tprm.(shell).a10"
              onClick={() => {
                setMfaOpen((v) => !v)
                // Under 1024 px the panel opens on the page, behind the menu:
                // the menu closes with the focus on the panel.
                menu.leave(() => document.querySelector<HTMLElement>('[data-testid="second-factor"]'))
              }}
              aria-expanded={mfaOpen}
              title="Two-step sign-in"
              aria-label="Two-step sign-in"
            >
              <Icon name="shield" size={16} />
            </button>
          )}
          {authConfig?.mode === 'oidc' || isLaunchSession() ? (
            <button type="button" data-action="tprm.(shell).a03" onClick={() => void signOut(authConfig)} title="Sign out" aria-label="Sign out">
              <Icon name="logout" size={16} />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setTokenOpen((v) => !v)
                menu.leave(() => document.getElementById('token-input'))
              }}
              aria-expanded={tokenOpen}
              title="Local development only: choose which demo person you are. UAT and production sign in through the identity provider and never show this."
            >
              Dev user
            </button>
          )}
        </div>
        {isDev && <span className="dev-flag">Dev auth</span>}
      </aside>
      {menu.open && <div className="side-scrim" onClick={menu.dismiss} aria-hidden="true" />}
      <div className="content" inert={menu.open || undefined}>
        <div className={'route-progress' + (moving ? ' on' : '')} aria-hidden="true" />
        <div className="topline">
          <MenuButton menu={menu} />
          {/* The search looks up vendors and opens their records, so it is
              offered where the register is readable and part of the job; for
              the IT admin (release 3.1) it would only ever answer 403. */}
          {me && can('vendor.read') && onScreen('vendor') && <QuickSearch />}
          {me && can('vendor.read') && <NoticeBell />}
          {me && <SuiteLinks />}
        </div>
        {mfaOpen && <SecondFactorPanel onClose={() => setMfaOpen(false)} />}
        {tokenOpen && (
          <form
            className="panel"
            style={{ margin: '12px 28px 0' }}
            onSubmit={(e) => {
              e.preventDefault()
              applyToken()
            }}
          >
            <label htmlFor="token-input">Development sign-in</label>
            <p className="muted" style={{ margin: '2px 0 6px', fontSize: 12.5 }}>
              Type who you are as <span className="mono">dev:&lt;tenant&gt;:&lt;roles&gt;:&lt;name&gt;</span>{' '}
              <Toggletip label="Why this box exists">
                This local build has no identity provider. On UAT and production people sign in through Cognito and
                this box does not exist.
              </Toggletip>
            </p>
            <div className="upload" style={{ marginTop: 4 }}>
              <input
                id="token-input"
                type="text"
                className="mono"
                style={{ flex: 1, minWidth: 280 }}
                value={draftToken}
                spellCheck={false}
                onChange={(e) => setDraftToken(e.target.value)}
              />
              <button type="submit" className="primary">
                Apply and reload
              </button>
            </div>
          </form>
        )}
      <main
        className="main page-enter"
        id="main"
        tabIndex={-1}
        // Settings and its six screens share one key, so the frame and its list
        // stay mounted while the entry changes (their own screens still remount).
        // The steps of an import are one page too, each step in the address
        // (release 3.3): remounting it on Next would throw the answers away.
        // So are a vendor's tabs (release 3.3, V1): a tab is pushed without a
        // hashchange, and Back to one must not remount the record.
        key={
          isSettingsScreen(route.name)
            ? 'settings'
            : route.name === 'import'
              ? '#/import'
              : route.name === 'vendor' || route.name === 'vendor-record'
                ? '#/vendor/' + route.id
                : window.location.hash.split('?')[0]
        }
        data-page={openHere && me ? navId(route.name) : undefined}
      >
        {me && <WorkQueueBar subject={me.subject} />}
        {authNotice && !meError && (
          <div className="note warn" role="alert">
            {authNotice}
          </div>
        )}
        {meError && (
          <div className="error-box" role="alert">
            <h3>
              {meError.code === 'ONE_PERSONA_PER_ACCOUNT' || meError.code === 'DIRECTORY_NAMES_TWO_PERSONAS'
                ? 'This account cannot sign in here'
                : meError.code === 'TENANT_SWITCHED_OFF' || meError.code.startsWith('LICENCE_')
                  ? 'This workspace is not available'
                  : 'Not signed in (' + meError.code + ')'}
            </h3>
            <p>{meError.message}</p>
            <p className="muted">{meError.remedy}</p>
            {/* Only where a pasted development token is how anybody signs in. */}
            {authConfig?.mode === 'dev' && (
              <button type="button" onClick={() => setTokenOpen(true)}>
                Edit token
              </button>
            )}
          </div>
        )}
        {rolesAllow && !openHere && !meError ? <NotYourJob persona={screensPersonaName()} home={start} screen={route.name} /> : null}
        {!rolesAllow && !meError ? (
          <div className="note stop" role="status" data-testid="role-refusal" data-page-refused={navId(route.name)}>
            <strong>This screen is not open to your roles.</strong>
            <p>{refusalReason(SCREEN_NEEDS[route.name])}</p>
          </div>
        ) : null}
        {openHere && me && route.name === 'home' && <Home me={me} />}
        {openHere && me && route.name === 'my-work' && <MyWork me={me} />}
        {openHere && route.name === 'vendors' && <Vendors me={me} />}
        {openHere && route.name === 'import' && <Import />}
        {openHere && me && route.name === 'vendor-add' && <VendorAdd me={me} />}
        {openHere && route.name === 'vendor-invite' && <VendorInvite vendorId={route.id} />}
        {openHere && route.name === 'assessments' && <Assessments />}
        {openHere && route.name === 'risk' && <Risk />}
        {openHere && route.name === 'incidents' && <Incidents />}
        {openHere && route.name === 'incident' && <Incidents incidentId={route.id} />}
        {openHere && route.name === 'audit' && <Audit />}
        {openHere && route.name === 'risk-matrix' && <RiskMatrixSettings />}
        {openHere && route.name === 'risk-sinks' && <RiskSinks />}
        {openHere && route.name === 'risk-sources' && <RiskSources />}
        {openHere && route.name === 'reports' && !route.report && <Reports />}
        {openHere && me && route.name === 'reports' && route.report === 'programme' && <ProgrammeReport me={me} />}
        {openHere && route.name === 'reports' && route.report === 'dates-to-check' && <DatesToCheck />}
        {openHere && route.name === 'portfolio' && <Portfolio />}
        {/* #/vendor/:id is the record's overview, #/vendor/:id/<tab> a tab and
            #/vendor/:id/full/<key> an old address of one; the record reads its
            own address (components/vendor/sections.ts), and stays mounted
            from one tab to the next. */}
        {openHere && (route.name === 'vendor' || route.name === 'vendor-record') && (
          <VendorDetail key={route.id} vendorId={route.id} initialTab={route.name === 'vendor-record' ? route.tab : 'overview'} />
        )}
        {openHere && route.name === 'assessment' && <AssessmentReview assessmentId={route.id} />}
        {openHere && route.name === 'imports' && <Imports />}
        {openHere && route.name === 'campaigns' && <Campaigns />}
        {openHere && route.name === 'campaign' && <CampaignDetail id={route.id} />}
        {openHere && route.name === 'campaign-batch' && <CampaignBatch />}
        {openHere && route.name === 'exceptions' && <Exceptions />}
        {openHere && route.name === 'findings' && <Findings />}
        {openHere && route.name === 'questionnaires' && <Questionnaires />}
        {openHere && route.name === 'questionnaire' && <QuestionnaireStudio id={route.id} />}
        {openHere && route.name === 'questionnaire-import' && <QuestionnaireImport />}
        {openHere && route.name === 'risk-models' && <RiskModels />}
        {openHere && route.name === 'risk-model' && <RiskModelStudio id={route.id} />}
        {/* Settings and its six screens are one frame: the list of every entry is on
            the page for each of them (views/Settings.tsx). */}
        {openHere && isSettingsScreen(route.name) && <SettingsRoute screen={route.name} />}
        {openHere && route.name === 'sustain' && <Sustain />}
        {openHere && route.name === 'evidence' && <Evidence />}
        {openHere && route.name === 'documents' && <Documents />}
        {openHere && route.name === 'procurement' && <Procurement />}
        {openHere && route.name === 'exits' && <Exits />}
        {openHere && route.name === 'exit-workspace' && <ExitWorkspace key={route.id} vendorId={route.id} />}
        {route.name === 'not-found' && <NotFound path={route.path} ways={reachable} />}
      </main>
      </div>
    </div>
    </ToastHost>
  )
}

// The screen for an address nothing answers to. It names the address so a
// broken link is reported as the link it is, not as "it went to Home", and it
// offers the reader's own screens rather than three fixed ones, which for a
// role that cannot open Vendors was three more refusals.
function NotFound({ path, ways }: { path: string; ways: Section[] }) {
  return (
    <div className="p1-page">
      <div className="p1-head">
        <div>
          <h1>There is no screen at this address</h1>
          <p>
            <span className="mono">{path}</span> is not a page in this build{' '}
            <Toggletip label="If a link brought you here">
              If a link brought you here, that link is the bug: report which one.
            </Toggletip>
          </p>
        </div>
      </div>
      <div className="p1-actions">
        {ways.length === 0 ? (
          // Nothing is reachable when the session itself failed. Settings is
          // where that is explained, so it is still the way out.
          <a className="button primary" href="#/settings">
            Settings
          </a>
        ) : (
          ways.slice(0, 3).map((w, i) => (
            <a className={'button' + (i === 0 ? ' primary' : '')} href={w.href} key={w.href}>
              {w.label}
            </a>
          ))
        )}
      </div>
    </div>
  )
}
